import styled from '@emotion/styled';
import { NavLink } from 'react-router-dom';
import {
  duration,
  easing,
  layout,
  palette,
  radius,
  shadow,
  spacing,
  transition,
  typography,
  zIndex,
} from '../../../style-utils/theme';
import { font } from '../../../style-utils/text';
import { down, up } from '../../../style-utils/media-queries';
import { ActiveIndicator, SCALE } from '../../../motion';

/**
 * THE RESIDENT PORTAL'S FURNITURE — the page, its brand bar, the tab strip and
 * the phone's bottom bar.
 *
 * The portal used to be one long page: parcels, then visitors, then the home.
 * It is a shell with three destinations now, and the same three are offered in
 * two shapes that never appear together — a tab strip under the brand bar at
 * md and up, and a fixed bottom bar below md, where a thumb can reach it.
 */

export const Page = styled.div`
  min-height: 100vh;
  min-height: 100dvh;
  background: ${palette.porcelain};
  display: flex;
  flex-direction: column;
  /* THE BOTTOM BAR IS FIXED, so it sits in nobody's flow. Without this the
     last row of every tab ended underneath it on a phone. The same expression
     the desk shell uses to clear its own bar (layout-container.styled). */
  padding-bottom: calc(${layout.navHeight} + var(--safe-bottom));

  ${up('md')} {
    padding-bottom: 0;
  }
`;

export const TopBar = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm};
  /* Extra right reserve for the fixed NotificationDock, which is not in this
     header's flow and would otherwise sit on the buttons. */
  padding: ${spacing.md} calc(${spacing.lg} + ${layout.bellDockReserve})
    ${spacing.md} ${spacing.lg};
  background: ${palette.surface};
  border-bottom: 1px solid ${palette.line};

  /* THE WHOLE RESIDENT PORTAL SCROLLED SIDEWAYS AT 320px, by 102px, before
     this rule: the brand on the left, two nowrap buttons on the right, and the
     reserve held clear for the fixed bell came to 421px of content in a 320px
     viewport. Wrapping is what makes it fit, and the reserve stays because the
     bell is still floating over this corner. */
  ${down('sm')} {
    flex-wrap: wrap;
    row-gap: ${spacing.xs};
    padding-right: calc(${spacing.sm} + ${layout.bellDockReserve});
    padding-left: ${spacing.sm};
  }
`;

export const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
`;

/* The display face, as the wordmark is everywhere else (design_system.md,
   Logo and assets). It used to inherit Inter at a raw 20px. */
export const Wordmark = styled.span`
  font-family: ${typography.display};
  font-size: ${typography.size.lg}px;
  font-weight: ${typography.weight.heavy};
  letter-spacing: ${typography.tracking.tight};
  color: ${palette.ink};
`;

export const TopActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};

  /* min-width: 0 because a flex item will not shrink below its content
     without it, which is how two short buttons pushed a whole page wide. */
  ${down('sm')} {
    min-width: 0;
    flex-wrap: wrap;
  }
`;

export const Content = styled.main`
  flex: 1;
  box-sizing: border-box;
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
  padding: ${spacing.md} ${spacing.sm} ${spacing.xl};

  &:focus {
    outline: none;
  }

  ${up('md')} {
    padding: ${spacing.xl} ${spacing.lg} ${spacing.xxl};
  }
`;

/* ── The tab strip, md and up (the account page's, same pieces) ─────────── */

export const Tabs = styled.nav`
  display: none;

  ${up('md')} {
    display: flex;
    flex-wrap: wrap;
    gap: ${spacing.xs};
    margin: 0 0 ${spacing.lg};
    padding: ${spacing.half};
    background: ${palette.surfaceMuted};
    border-radius: ${radius.md};
  }
`;

export const Tab = styled(NavLink)`
  /* The active pill is TabIndicator, an absolutely positioned child that
     slides between tabs; the tab is its containing block and its own stacking
     context so the pill can sit under the label without leaking out. */
  position: relative;
  isolation: isolate;
  flex: 1 1 auto;
  text-align: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: ${layout.touchTarget};
  box-sizing: border-box;
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.sm};
  font-family: ${font.family};
  font-size: ${font.size.xs}px;
  font-weight: 600;
  color: ${palette.slate};
  text-decoration: none;
  transition: ${transition.colors};

  &:hover {
    color: ${palette.ink};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  /* Colour only: the surface and the shadow travel with TabIndicator. */
  &.active {
    color: ${palette.ink};
  }
`;

export const TabLabel = styled.span`
  position: relative;
  z-index: 1;
`;

/* THE PILL MOVES. One element, rendered inside the active NavLink; framer's
   layoutId carries it to the next tab with the indicator spring. */
export const TabIndicator = styled(ActiveIndicator)`
  position: absolute;
  inset: 0;
  z-index: 0;
  border-radius: ${radius.sm};
  background: ${palette.surface};
  box-shadow: ${shadow.sm};
  pointer-events: none;
`;

/* ── The bottom bar, below md (the desk rail's, same material) ───────────── */

export const BottomBar = styled.nav`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: space-around;
  gap: ${spacing.xs};
  box-sizing: border-box;
  width: 100%;
  min-height: ${layout.navHeight};
  padding: 0 ${spacing.md} var(--safe-bottom);
  border-top: 1px solid ${palette.line};
  background: ${palette.surfaceGlass};
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  box-shadow: ${shadow.lg};
  z-index: ${zIndex.sticky};

  ${up('md')} {
    display: none;
  }
`;

/* One destination. The label IS the control, so the whole slot is the hit
   area; weight never changes with state (a bolder active label reflows the
   bar), colour and the underline carry it. */
export const BarItem = styled(NavLink)`
  position: relative;
  flex: 1 1 0;
  min-width: ${layout.touchTarget};
  max-width: ${layout.navItemMaxWidth};
  min-height: ${layout.touchTarget};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: ${spacing.xs};
  border-radius: ${radius.sm};
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.medium};
  color: ${palette.slate};
  text-decoration: none;
  white-space: nowrap;
  transition:
    ${transition.colors},
    transform ${duration.instant} ${easing.standard};

  &:hover {
    color: ${palette.lagoon};
    background: ${palette.irisTintSoft};
    transform: translateY(-1px);
  }

  &:active {
    transform: scale(${SCALE.pressed});
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  &.active {
    color: ${palette.lagoon};
  }
`;

export const BarLabel = styled.span`
  display: block;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* The 24x3 underline of the active slot — one element that slides between
   the three, like the desk rail's. Offsets are arithmetic, not a translate,
   because the layout animation owns this element's transform. */
export const BarIndicator = styled(ActiveIndicator)`
  position: absolute;
  left: calc(50% - 12px);
  bottom: 0;
  width: 24px;
  height: 3px;
  border-radius: ${radius.pill};
  background: ${palette.iris};
  pointer-events: none;
`;
